import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, KeyRound } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { requestPasswordReset, confirmPasswordReset } from "@/lib/passwordReset";
import Logo from "@/components/Logo";

type Step = "request" | "confirm";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>("request");
  const [username, setUsername] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    setLoading(true);
    try {
      await requestPasswordReset(username);
      // Generic, anti-enumeration message — true regardless of account existence.
      toast({
        title: "Check your email",
        description: "If an account with that username has a recovery email, a 6-digit code is on its way.",
      });
      setStep("confirm");
    } catch {
      // Even on transport error, move forward so we don't leak signal.
      setStep("confirm");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) {
      toast({ title: "Invalid code", description: "Enter the 6-digit code from your email.", variant: "destructive" });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: "Password too short", description: "Use at least 8 characters.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirm) {
      toast({ title: "Passwords don't match", description: "Re-enter the same password.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const ok = await confirmPasswordReset(username, code, newPassword);
      if (ok) {
        toast({ title: "Password updated", description: "Sign in with your new password." });
        navigate("/venue");
      } else {
        toast({ title: "Code invalid or expired", description: "Double-check the code, or request a new one.", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Couldn't reset password", description: err?.message ?? "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-console-bg text-console-text font-geist relative">
      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="border-b border-console-line bg-console-bg/85 backdrop-blur-sm">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <button onClick={() => navigate("/")} className="flex items-center gap-3 min-h-[44px]">
              <Logo tone="text" size="md" />
              <div className="flex flex-col leading-none text-left">
                <span className="font-geist font-semibold text-[15px] tracking-tight text-console-text">KaraokeCore</span>
                <span className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted mt-1">Password reset</span>
              </div>
            </button>
            <Button
              variant="ghost"
              onClick={() => navigate("/venue")}
              className="h-11 sm:h-9 px-3 sm:px-4 text-[13px] font-medium font-geist text-console-text hover:bg-white/5 hover:text-console-text border border-console-line-strong rounded-[6px]"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to login
            </Button>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center p-4 sm:p-6">
          <div className="w-full max-w-md">
            <div className="bg-console-surface border border-console-line rounded-[12px] p-7 sm:p-9">
              <div className="font-geist-mono text-[11px] tracking-[0.18em] uppercase text-console-primary mb-4 flex items-center gap-2">
                <KeyRound className="h-3.5 w-3.5" />
                {step === "request" ? "Reset / Step 1" : "Reset / Step 2"}
              </div>
              <h1 className="font-geist font-semibold text-[32px] leading-none tracking-[-0.03em] mb-3">
                {step === "request" ? "Forgot password" : "Enter your code"}
              </h1>
              <p className="font-geist text-[14px] leading-relaxed text-console-muted mb-7">
                {step === "request"
                  ? "Enter your username. If a recovery email is on file, we'll send a 6-digit code."
                  : "Enter the 6-digit code we emailed, then choose a new password."}
              </p>

              {step === "request" ? (
                <form onSubmit={handleRequest} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="rp-username" className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">Username</Label>
                    <Input
                      id="rp-username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="your username"
                      autoComplete="username"
                      className="h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim font-geist text-[15px] rounded-[6px] focus:border-console-primary focus-visible:ring-0"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 px-5 mt-3 text-[14px] font-semibold font-geist bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px]"
                  >
                    {loading ? "Sending…" : "Send code"}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleConfirm} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="rp-code" className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">6-digit code</Label>
                    <Input
                      id="rp-code"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="123456"
                      inputMode="numeric"
                      className="h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim font-geist text-[15px] tracking-[0.3em] rounded-[6px] focus:border-console-primary focus-visible:ring-0"
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rp-new" className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">New password</Label>
                    <Input
                      id="rp-new"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="at least 8 characters"
                      autoComplete="new-password"
                      className="h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim font-geist text-[15px] rounded-[6px] focus:border-console-primary focus-visible:ring-0"
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rp-confirm" className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">Confirm password</Label>
                    <Input
                      id="rp-confirm"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="re-enter password"
                      autoComplete="new-password"
                      className="h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim font-geist text-[15px] rounded-[6px] focus:border-console-primary focus-visible:ring-0"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 px-5 mt-3 text-[14px] font-semibold font-geist bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px]"
                  >
                    {loading ? "Updating…" : "Reset password"}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setStep("request")}
                    className="w-full text-center font-geist-mono text-[11px] text-console-dim hover:text-console-muted underline underline-offset-2"
                  >
                    Didn't get a code? Try again
                  </button>
                </form>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ResetPassword;
